/**
 * Timeline.jsx - one record's whole journey (AL-01): what people logged plus
 * what the system recorded (stage, designation and status changes, task
 * events, opportunity events - AL-03 / AL-04), newest first in month groups.
 *
 *   kind 'account'     GET /api/accounts/<id>/timeline - a company also shows
 *                      every site's items, labelled with the site name
 *   kind 'contact'     GET /api/accounts/contacts/<id>/timeline
 *   kind 'opportunity' GET /api/accounts/opportunities/<id>/timeline - only
 *                      entries TAGGED to it (AL-09), plus its own events
 *
 * Filter chips (AL-02) narrow it; none picked shows everything. Each entry is
 * one line (AL-08): date, type, subject, who, where, files. Click to open the
 * full notes, attendees, opportunity tags, files, the recording link, and
 * Edit / Delete on a logged entry; a system event opens to its from and to.
 *
 * Edits and deletes go to the ENTRY's own account and are keyed by id, never
 * by list position (a filtered or merged list used to delete the wrong one).
 *
 * It re-reads itself whenever a task changes anywhere (notifyTasksChanged),
 * so a task completed on the same screen shows its "Task completed" row and
 * note straight away - and when someone else logs, edits or files something
 * (src/live). That background re-read is quiet: no "Refreshing...", and when
 * it fails the entries stay with no error line. Only a read the person is
 * waiting for (the first, after a save here, Try again) says it failed.
 *
 * Inside the opportunity editor (currentOpportunityId), the open
 * opportunity's own chip is plain text and any other opens in a new browser
 * tab: a same-tab link would swap or drop the editor that is open here.
 *
 * Never on the GLOBAL KSA tab: it renders nothing and fetches nothing there.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Icon } from '../ui/Icon';
import { Toast } from '../ui/Toast';
import { useApp } from '../../context/AppContext';
import { useDataScope } from '../../context/DataScope';
import { ERROR_LINE, BTN_CANCEL, tagStyle } from './crmStyles';
import { indexById, contactIndex, opportunityLabel, opportunityHash } from './nextStep';
import { isLeftCompany } from '../../data/crmStages';
import { fmtDate } from '../../utils/format';
import { useTasksChanged } from '../tasks/useTasks';
import { useLiveRefresh, latestReads } from '../../live/useLive';
import {
  CHIPS,
  CHIP_COLORS,
  chipCounts,
  filterTimeline,
  groupByMonth,
  rowSummary,
  localDateTime,
  sourceLabel,
  taskStatusLabel,
} from './timelineModel';
import ActivityFiles from './ActivityFiles';
import ActivityForm from './ActivityForm';

// the pure helpers (CHIPS, chipOf, sortTimeline, groupByMonth, rowSummary,
// taggableOpportunities, attendeeContacts, ...) - see timelineModel.js for
// why they do not live in a timeline.js next to this file
export * from './timelineModel';

const enc = encodeURIComponent;

/** The endpoint for one kind of record. */
export function timelineUrl(kind, id) {
  if (kind === 'contact') return `/api/accounts/contacts/${enc(id)}/timeline`;
  if (kind === 'opportunity') return `/api/accounts/opportunities/${enc(id)}/timeline`;
  return `/api/accounts/${enc(id)}/timeline`;
}

const readError = async (r, fallback) => {
  const b = await r.json().catch(() => ({}));
  return b.error || fallback;
};

const MINI_BTN = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 0,
  fontSize: 10,
  fontWeight: 800,
  fontFamily: 'Montserrat',
};
const DETAIL_LABEL = { fontSize: 9, fontWeight: 800, color: '#8A9289', letterSpacing: 1, textTransform: 'uppercase', marginBottom: 3 };

const Detail = ({ label, children, wide }) => (
  <div style={{ minWidth: 0, gridColumn: wide ? '1 / -1' : undefined }}>
    <div style={DETAIL_LABEL}>{label}</div>
    <div style={{ fontSize: 11, color: '#2A322A', lineHeight: 1.5 }}>{children}</div>
  </div>
);

export default function Timeline({ kind, id, refreshKey, editable, deletable, onChanged, currentOpportunityId }) {
  const scope = useDataScope();
  const { allAccounts, allProjects } = useApp();
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState('loading'); // 'loading' | 'ready' | 'error'
  const [loadError, setLoadError] = useState('');
  const [chips, setChips] = useState([]);
  const [openIds, setOpenIds] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null);
  const [toast, setToastState] = useState(null); // { message, tone }
  const setToast = (message, tone) => setToastState(message ? { message, tone } : null);
  const shownFor = useRef(null); // the record the list on screen belongs to
  // Reads overlap - the first, a save's re-read, a task change, someone
  // else's change - so the newest answer GOT wins. (A newer read used to
  // cancel the one before it: when that newer one failed, the entry just
  // logged here stayed missing.)
  const [reads] = useState(latestReads);

  /** Read the timeline. `live`: someone else's change - quiet, see the top of the file. */
  const load = useCallback(
    ({ live = false } = {}) => {
      if (scope || !id) return;
      const key = `${kind}:${id}`;
      const ticket = reads.start();
      if (!live) setStatus('loading');
      (async () => {
        try {
          const r = await fetch(timelineUrl(kind, id));
          if (!r.ok) throw new Error(await readError(r, `the server answered ${r.status}`));
          const rows = await r.json();
          if (!Array.isArray(rows)) throw new Error('the server sent something that is not a list');
          if (shownFor.current !== key || !reads.show(ticket)) return; // another record's, or older than what is shown
          setItems(rows);
          setStatus('ready');
        } catch (e) {
          console.error('timeline error:', e);
          if (live || shownFor.current !== key || !reads.show(ticket)) return;
          setLoadError(e && e.message ? e.message : 'unknown error');
          setStatus('error');
        }
      })();
    },
    [scope, kind, id, reads]
  );
  const reload = useCallback(() => load(), [load]);
  const reloadQuietly = useCallback(() => load({ live: true }), [load]);
  // a task completed, added or deleted anywhere (this screen's Tasks list, the
  // Dashboard, the Tasks page) is a timeline event: re-read so it shows now
  // (load hears { live: true } when it was someone else's task)
  useTasksChanged(load);
  // and so is someone else's logged activity, file or opportunity change
  // (src/live) - an opportunity's own row, not its notes or Doc Hub files
  useLiveRefresh(['accounts', 'crm', 'projects'], reloadQuietly, { match: (e) => e.kind !== 'projects' || !e.sub });

  useEffect(() => {
    if (scope || !id) return;
    // another record: never show the last one's entries while this one loads
    const key = `${kind}:${id}`;
    if (shownFor.current !== key) {
      shownFor.current = key;
      setItems([]);
      setOpenIds(new Set());
      setEditingId(null);
    }
    load();
  }, [scope, kind, id, refreshKey, load]);

  const accountsById = useMemo(() => indexById(allAccounts), [allAccounts]);
  const contactsById = useMemo(() => contactIndex(allAccounts), [allAccounts]);
  const projectsById = useMemo(() => indexById(allProjects), [allProjects]);
  const counts = useMemo(() => chipCounts(items), [items]);
  const groups = useMemo(() => groupByMonth(filterTimeline(items, chips)), [items, chips]);

  if (scope || !id) return null;

  const ctx = { accountsById, homeId: kind === 'account' ? id : null };
  const toggleChip = (key) => setChips((cur) => (cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key]));
  const toggleOpen = (itemId) =>
    setOpenIds((cur) => {
      const next = new Set(cur);
      if (next.has(itemId)) next.delete(itemId);
      else next.add(itemId);
      return next;
    });

  const removeEntry = async (item) => {
    const a = item.activity;
    const home = (a && a.account_id) || item.account_id;
    if (!a || !a.id || !home) return;
    const n = (a.attachments || []).length;
    if (!window.confirm(n ? `Delete this activity entry and its ${n} file${n === 1 ? '' : 's'}?` : 'Delete this activity entry?')) return;
    try {
      const r = await fetch(`/api/accounts/${enc(home)}/activity/${enc(a.id)}`, { method: 'DELETE' });
      if (!r.ok) {
        setToast(await readError(r, 'Could not delete the activity.'), 'error');
        return;
      }
    } catch (e) {
      console.error('deleteActivity error:', e);
      setToast('Could not delete the activity.', 'error');
      return;
    }
    setToast('Activity deleted');
    reload();
    if (onChanged) onChanged();
  };

  const contactName = (cid) => {
    const c = contactsById.get(String(cid));
    if (!c) return 'Contact not on file';
    return `${c.name || 'Unnamed'}${isLeftCompany(c) ? ' (left company)' : ''}`;
  };

  // an opportunity link. In its own editor the open one is plain text and any
  // other opens in a new tab, so the editor here is never swapped or dropped.
  const current = currentOpportunityId != null && currentOpportunityId !== '' ? String(currentOpportunityId) : null;
  const oppLink = (oid, label, style) => {
    if (current && String(oid) === current)
      return (
        <span key={oid} style={style} title="This opportunity">
          {label}
        </span>
      );
    const newTab = current ? { target: '_blank', rel: 'noopener noreferrer' } : {};
    return (
      <a
        key={oid}
        href={opportunityHash(oid)}
        {...newTab}
        onClick={(e) => e.stopPropagation()}
        style={style}
        title={current ? 'Open this opportunity in a new tab' : 'Open this opportunity'}
      >
        {label}
        {current ? ' ↗' : ''}
      </a>
    );
  };

  const renderExpanded = (item, s) => {
    const a = item.activity;
    if (item.kind === 'activity' && a) {
      const contactIds = a.contact_ids || [];
      const people = a.internal_attendees || [];
      const opps = a.opportunity_ids || [];
      const files = a.attachments || [];
      const recording = a.recording_url;
      return (
        <>
          {(item.text || a.text) && (
            <div
              style={{
                fontSize: 11.5,
                color: '#2A322A',
                lineHeight: 1.55,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                marginBottom: 10,
              }}
            >
              {item.text != null ? item.text : a.text}
            </div>
          )}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px 18px' }}>
            {contactIds.length > 0 && <Detail label="Customer attendees">{contactIds.map(contactName).join(', ')}</Detail>}
            {people.length > 0 && <Detail label="Internal attendees">{people.join(', ')}</Detail>}
            {opps.length > 0 && (
              <Detail label="Opportunities">
                <span style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {opps.map((oid) => {
                    const p = projectsById.get(String(oid));
                    return oppLink(oid, p ? opportunityLabel(p) : oid, { ...tagStyle('#1B5E20', '#E8F5E9'), textDecoration: 'none' });
                  })}
                </span>
              </Detail>
            )}
            {files.length > 0 && (
              <Detail wide label={`Files (${files.length})`}>
                <ActivityFiles files={files} />
              </Detail>
            )}
            {recording && (
              <Detail label="Recording">
                <a
                  href={recording}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ color: '#1565C0', fontWeight: 700 }}
                >
                  recording ↗
                </a>
              </Detail>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              gap: 12,
              alignItems: 'center',
              marginTop: 10,
              fontSize: 9.5,
              color: '#8A9289',
              fontWeight: 700,
              flexWrap: 'wrap',
            }}
          >
            {s.who && <span>Logged by {s.who}</span>}
            {item.at && <span>{localDateTime(item.at)}</span>}
            <span style={{ flex: 1 }} />
            {editable && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setEditingId(item.id);
                }}
                title="Edit this log"
                style={{ ...MINI_BTN, color: '#1B5E20' }}
              >
                Edit
              </button>
            )}
            {deletable && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeEntry(item);
                }}
                title="Delete this log"
                style={{ ...MINI_BTN, color: '#B71C1C', display: 'inline-flex', alignItems: 'center', gap: 4 }}
              >
                <Icon name="trash" size={11} color="#B71C1C" /> Delete
              </button>
            )}
          </div>
        </>
      );
    }
    if (item.kind === 'task') {
      const t = item.task || {};
      return (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px 18px' }}>
          <Detail label="Task">{t.description || item.subject || '-'}</Detail>
          {t.owner && <Detail label="Owner">{t.owner}</Detail>}
          {t.due_date && <Detail label="Due">{fmtDate(t.due_date)}</Detail>}
          <Detail label="Status now">{taskStatusLabel(t.status)}</Detail>
          {item.text && (
            <Detail label="Note">
              <span style={{ whiteSpace: 'pre-wrap' }}>{item.text}</span>
            </Detail>
          )}
          <Detail label="When">
            {localDateTime(item.at) || '-'}
            {s.who ? ` by ${s.who}` : ''}
          </Detail>
        </div>
      );
    }
    // stage / designation / status / opportunity events
    const o = item.opportunity;
    const src = sourceLabel(item.source);
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '10px 18px' }}>
        {o && o.id && (
          <Detail label="Opportunity">{oppLink(o.id, o.label || item.subject || o.id, { color: '#1B5E20', fontWeight: 700 })}</Detail>
        )}
        <Detail label="From">{item.from || (item.kind === 'stage' ? 'Added to the pipeline' : 'none')}</Detail>
        <Detail label="To">{item.to || 'none'}</Detail>
        <Detail label="When">
          {localDateTime(item.at) || '-'}
          {s.who ? ` by ${s.who}` : ''}
          {src ? ` (${src})` : ''}
        </Detail>
      </div>
    );
  };

  const total = items.length;
  return (
    <div>
      {toast && <Toast message={toast.message} tone={toast.tone} onDone={() => setToast(null)} />}

      {/* AL-02 filter chips: several can be on; none = everything */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <button
          type="button"
          aria-pressed={chips.length === 0}
          onClick={() => setChips([])}
          style={chipButton(chips.length === 0, CHIP_COLORS.other)}
        >
          All{total ? ` (${total})` : ''}
        </button>
        {CHIPS.map((c) => (
          <button
            key={c.key}
            type="button"
            aria-pressed={chips.includes(c.key)}
            onClick={() => toggleChip(c.key)}
            style={chipButton(chips.includes(c.key), CHIP_COLORS[c.key])}
          >
            {c.label}
            {counts[c.key] ? ` (${counts[c.key]})` : ''}
          </button>
        ))}
        {status === 'loading' && total > 0 && <span style={{ fontSize: 10, color: '#999' }}>Refreshing...</span>}
      </div>

      {status === 'error' && (
        <div role="alert" style={{ ...ERROR_LINE, display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
          <span style={{ flex: 1 }}>Could not load the timeline: {loadError}</span>
          <button type="button" onClick={reload} style={{ ...BTN_CANCEL, padding: '4px 10px', fontSize: 10 }}>
            Try again
          </button>
        </div>
      )}

      {status === 'loading' && total === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#aaa', fontSize: 11 }}>Loading the timeline...</div>
      ) : status !== 'error' && total === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#ccc', fontSize: 11 }}>No meetings or activity logged yet</div>
      ) : total > 0 && groups.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 30, color: '#999', fontSize: 11 }}>
          Nothing matches these filters.{' '}
          <button type="button" onClick={() => setChips([])} style={{ ...MINI_BTN, color: '#1B5E20', fontSize: 11 }}>
            Show everything
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {groups.map((g) => (
            <div key={g.key}>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 800,
                  color: '#8A9289',
                  letterSpacing: 1.2,
                  textTransform: 'uppercase',
                  marginBottom: 6,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                }}
              >
                {g.label}
                <span style={{ flex: 1, height: 1, background: '#EFEFEF' }} />
                <span style={{ color: '#B8C0B6' }}>{g.items.length}</span>
              </div>
              <div style={{ background: '#fff', border: '1px solid #E4E9E1', borderRadius: 10, overflow: 'hidden' }}>
                {g.items.map((item, i) => {
                  const s = rowSummary(item, ctx);
                  const isOpen = openIds.has(item.id);
                  const color = CHIP_COLORS[s.chip] || CHIP_COLORS.other;
                  const top = i > 0 ? '1px solid #F0F3EE' : 'none';
                  if (editingId === item.id && item.kind === 'activity' && item.activity) {
                    const a = item.activity;
                    const home = allAccounts && allAccounts.find((x) => x && String(x.id) === String(a.account_id || item.account_id));
                    return (
                      <div key={item.id} style={{ borderTop: top, padding: 10, background: '#FAFBF8' }}>
                        <ActivityForm
                          record={home || { id: a.account_id || item.account_id, contacts: [] }}
                          initial={{ ...a, account_id: a.account_id || item.account_id }}
                          onCancel={() => setEditingId(null)}
                          // a form dropped after a partial save reports late: never close another entry's edit
                          onSaved={() => {
                            setEditingId((cur) => (cur === item.id ? null : cur));
                            setToast('Activity updated');
                            reload();
                            if (onChanged) onChanged();
                          }}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={item.id} style={{ borderTop: top, background: isOpen ? '#FAFBF8' : '#fff' }}>
                      <div
                        role="button"
                        tabIndex={0}
                        aria-expanded={isOpen}
                        onClick={() => toggleOpen(item.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggleOpen(item.id);
                          }
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '7px 12px',
                          cursor: 'pointer',
                          minHeight: 30,
                          boxSizing: 'border-box',
                        }}
                      >
                        <span
                          style={{
                            width: 70,
                            flexShrink: 0,
                            fontSize: 10,
                            fontWeight: 700,
                            color: '#8A9289',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {s.date || '-'}
                        </span>
                        <span style={{ ...tagStyle(color.fg, color.bg), flexShrink: 0 }}>{s.typeLabel}</span>
                        <span
                          style={{
                            flex: 1,
                            minWidth: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: 11.5,
                            fontWeight: 700,
                            color: '#0D0D0D',
                          }}
                          title={[s.subject, s.change].filter(Boolean).join(' ')}
                        >
                          {s.subject}
                          {s.change && (
                            <span style={{ fontWeight: 600, color: '#777' }}>
                              {s.subject ? '  ' : ''}
                              {s.change}
                            </span>
                          )}
                        </span>
                        {s.where && (
                          <span
                            style={{
                              ...tagStyle('#7B1FA2', '#F6EFFA'),
                              flexShrink: 0,
                              maxWidth: 170,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                            title={s.where}
                          >
                            {s.where}
                          </span>
                        )}
                        {s.who && (
                          <span style={{ fontSize: 10, color: '#8A9289', fontWeight: 700, whiteSpace: 'nowrap', flexShrink: 0 }}>
                            {s.who}
                          </span>
                        )}
                        {s.files > 0 && (
                          <span
                            style={{ fontSize: 10, color: '#1565C0', fontWeight: 800, whiteSpace: 'nowrap', flexShrink: 0 }}
                            title={`${s.files} file${s.files === 1 ? '' : 's'} attached`}
                          >
                            {s.files} file{s.files === 1 ? '' : 's'}
                          </span>
                        )}
                        <span
                          style={{
                            display: 'inline-flex',
                            transform: `rotate(${isOpen ? 90 : 0}deg)`,
                            transition: 'transform 0.15s',
                            flexShrink: 0,
                          }}
                        >
                          <Icon name="chevron" size={12} color="#B8C0B6" />
                        </span>
                      </div>
                      {isOpen && <div style={{ padding: '2px 14px 12px 90px' }}>{renderExpanded(item, s)}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function chipButton(on, color) {
  return {
    fontSize: 10,
    fontWeight: 800,
    fontFamily: 'Montserrat',
    cursor: 'pointer',
    borderRadius: 999,
    padding: '4px 11px',
    border: `1px solid ${on ? color.fg : '#E0E0E0'}`,
    background: on ? color.bg : '#fff',
    color: on ? color.fg : '#777',
  };
}
